import { Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { ReturnSchedule } from '~app/graph/generated/graphql'
import {
  type ConfirmableField,
  getFieldsNeedingConfirmation,
  type ReturnsValues,
} from './returns-transitions'

/**
 * Asks before a save that changes where a customer's originals go.
 *
 * It names the change and nothing else. What the change goes on to do depends
 * on server state this screen cannot see — whether a schedule already exists,
 * what the subscription carries, which originals are in flight — so spelling
 * out consequences here would state things that are not reliably true.
 */
const useReturnsConfirmation = () => {
  const { t } = useTranslation()

  const getQuestion = ({
    baseline,
    field,
    values,
  }: Args & { field: ConfirmableField }) => {
    if (field === 'scanCenter') {
      return t('customerDetails.confirm.scanCenter', {
        from: baseline.scanCenter,
        to: values.scanCenter,
      })
    }

    return values.returnsInterval === ReturnSchedule.Off
      ? t('customerDetails.confirm.scheduleOff')
      : t('customerDetails.confirm.scheduleOn', {
          interval: t(
            `customerDetails.returnIntervals.${values.returnsInterval}`
          ),
        })
  }

  return {
    confirmReturnsSave: ({ baseline, values }: Args): Promise<boolean> => {
      const questions = getFieldsNeedingConfirmation({ baseline, values }).map(
        field => getQuestion({ baseline, field, values })
      )

      if (questions.length === 0) {
        return Promise.resolve(true)
      }

      return new Promise(resolve => {
        Modal.confirm({
          autoFocusButton: 'cancel',
          cancelButtonProps: { type: 'primary' },
          cancelText: t('labels.no'),
          // more than one change in a single save: list them rather than
          // folding them into one sentence that names neither
          content:
            questions.length > 1 ? (
              <ul>
                {questions.map(question => (
                  <li key={question}>{question}</li>
                ))}
              </ul>
            ) : undefined,
          okButtonProps: { danger: true, type: 'primary' },
          okText: t('labels.yes'),
          onCancel: () => resolve(false),
          onOk: () => resolve(true),
          title:
            questions.length > 1
              ? t('customerDetails.confirm.combinedTitle')
              : questions[0],
        })
      })
    },
  }
}

interface Args {
  baseline: ReturnsValues
  values: ReturnsValues
}

export default useReturnsConfirmation
